/**
 * Control layouts. Keys are `KeyboardEvent.code` values, so the bindings work
 * the same on a Cyrillic layout — code reflects the physical key, not the
 * character it produces.
 */
export interface KeyboardBinding {
  up: string[]
  down: string[]
  left: string[]
  right: string[]
  push: string[]
  dash: string[]
  wave: string[]
  brace: string[]
  vortex: string[]
}

/** Player 1: left hand on WASD, abilities on the surrounding keys. */
export const P1_KEYS: KeyboardBinding = {
  up: ['KeyW'],
  down: ['KeyS'],
  left: ['KeyA'],
  right: ['KeyD'],
  push: ['Space'],
  dash: ['ShiftLeft'],
  wave: ['KeyQ'],
  brace: ['KeyE'],
  vortex: ['KeyR'],
}

/** Player 2 fallback when no gamepad is connected: arrows + numpad. */
export const P2_KEYS: KeyboardBinding = {
  up: ['ArrowUp'],
  down: ['ArrowDown'],
  left: ['ArrowLeft'],
  right: ['ArrowRight'],
  push: ['Numpad0', 'ControlRight'],
  dash: ['ShiftRight', 'NumpadEnter'],
  wave: ['Numpad1', 'Slash'],
  brace: ['Numpad2', 'Period'],
  vortex: ['Numpad3', 'Comma'],
}

/** Standard-mapping gamepad button indices (Xbox controller labels). */
export const PAD_BUTTONS = {
  A: 0,
  B: 1,
  X: 2,
  Y: 3,
  LB: 4,
  RB: 5,
  LT: 6,
  RT: 7,
  Back: 8,
  Start: 9,
  LStick: 10,
  RStick: 11,
  DUp: 12,
  DDown: 13,
  DLeft: 14,
  DRight: 15,
} as const

export const PAD_BINDING = {
  push: PAD_BUTTONS.A,
  dash: PAD_BUTTONS.B,
  wave: PAD_BUTTONS.X,
  vortex: PAD_BUTTONS.Y,
  brace: PAD_BUTTONS.LT,
  pause: PAD_BUTTONS.Start,
} as const

export const STICK_DEADZONE = 0.22
export const TRIGGER_THRESHOLD = 0.35
/**
 * A press stays "warm" this long, so it fires the moment lockout ends.
 *
 * Raised from 0.12 s: a cast now locks the attacker out for 267–700 ms, and a
 * buffer shorter than the lockout it is meant to cover means the press made
 * during recovery is simply lost. Smash Ultimate buffers 9 frames against
 * comparable windows. The counterweight is in `InputSystem`, which only arms
 * the buffer when the ability is genuinely about to be available.
 */
export const INPUT_BUFFER_SECONDS = 0.35
